import { type DragControls, motion } from 'framer-motion';
import type React from 'react';
import type { Novel } from '../../data/samples';
import type { ReadingHistoryItem } from '../../hooks/useReadingHistory';
import { SPRING_BOUNCY } from '../../utils/animationPresets';
import { getDisplayProgressPercentage } from '../../utils/readerMetrics';
import { isDarkTheme } from '../../utils/theme';
import { GripVerticalIcon, TrashIcon } from '../Icons';
import type { NovelTheme } from '../ReaderSettings';

import { NovelCover } from './NovelCover';

export interface NovelCoverConfig {
  bgColor?: string;
  bgImage?: string;
  titleColor?: string;
  titleSize?: string;
  titleFont?: 'serif' | 'sans' | 'lanobe';
  titlePos?: 'top' | 'center' | 'bottom';
  authorColor?: string;
  authorPos?: 'top' | 'bottom';
  hasBorder?: boolean;
  borderColor?: string;
  hasNoise?: boolean;
  hasGloss?: boolean;
  obiText?: string;
  obiBg?: string;
  obiColor?: string;

  // 新規追加デコパラメータ
  titleBorder?: 'none' | 'underline' | 'double-underline' | 'box';
  titleOffsetY?: number;
  titleOffsetX?: number;
  bgBlur?: number;
  bgBrightness?: number;
  titleBgType?: 'none' | 'semi-transparent' | 'solid' | 'gradient' | 'bordered' | 'fade-x';
  titleBgColor?: string;
  titleBgOpacity?: number;

  // 新規追加デコパラメータ第3弾
  baseBgColor?: string;
  customTitle?: string;
  effectType?:
    | 'none'
    | 'cherry-blossom'
    | 'confetti'
    | 'sparkle'
    | 'vignette'
    | 'cyber'
    | 'hologram'
    | 'raindrop'
    | 'halftone'
    | 'fire'
    | 'galaxy'
    | 'crack'
    | 'ripple'
    | 'snow'
    | 'retro-noise'
    | 'watercolor';
  effectBlendMode?: 'normal' | 'multiply' | 'screen' | 'overlay' | 'color-dodge' | 'color-burn';
  effectStrength?: number;
  effectColor?: string;
  glossStrength?: number;

  // --- デコアイディア全実装追加分 ---
  // A. テキスト系
  subtitleText?: string;
  subtitleColor?: string;
  subtitleSize?: string;
  subtitleOpacity?: number;

  copywritingText?: string;
  copywritingColor?: string;
  copywritingSize?: string;
  copywritingOpacity?: number;
  copywritingPos?: 'top' | 'middle' | 'bottom';

  titleRuby?: string;
  titleOutlineColor?: string;
  titleOutlineWidth?: number;
  titleOutlineOpacity?: number;

  titleShadowColor?: string;
  titleShadowBlur?: number;
  titleShadowX?: number;
  titleShadowY?: number;
  titleShadowOpacity?: number;

  titleRotate?: number;
  titleLetterSpacing?: number;

  // B. 画像加工系
  bgSepia?: number;
  bgGray?: number;
  bgSaturate?: number;
  bgHueRotate?: number;
  bgOverlayColor?: string;
  bgOverlayOpacity?: number;
  bgCropPos?: 'center' | 'top' | 'bottom';
  bgDuotoneDark?: string;
  bgDuotoneLight?: string;
  bgDuotoneEnable?: boolean;

  // C. エフェクト位置調整
  effectOffsetX?: number;
  effectOffsetY?: number;
  effectScale?: number;

  // D. レイアウト・フレーム系
  cornerStyle?: 'none' | 'arabesque' | 'classic' | 'modern';
  cornerColor?: string;
  cornerOpacity?: number;

  badgeText?: string;
  badgePos?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  badgeBgColor?: string;
  badgeTextColor?: string;
  badgeBgOpacity?: number;

  isVerticalTitle?: boolean;
  hasLeftTitleBar?: boolean;
  leftTitleBarColor?: string;
  leftTitleBarOpacity?: number;

  bookSpineWidth?: number;
  bookSpineColor?: string;
  bookSpineOpacity?: number;

  isPolaroid?: boolean;
  tapeStyle?: 'none' | 'washi' | 'clear';
  tapeColor?: string;
  tapeOpacity?: number;
  effectRotate?: number;

  // E. カラー&グラデーション系
  gradType?: 'linear' | 'radial' | 'mesh';
  bgColor2?: string;
  bgColor3?: string;
  gradAngle?: number;
  gradPreset?: string;

  // 各種色の不透明度（アルファチャンネル）
  titleColorOpacity?: number;
  authorColorOpacity?: number;
  borderColorOpacity?: number;
  obiBgOpacity?: number;
  obiColorOpacity?: number;

  // デコ強化用パラメータ
  stamps?: Array<{
    id: string;
    type: string;
    x: number;
    y: number;
    scale: number;
    rotate: number;
    opacity: number;
    color: string;
  }>;
  customTexts?: Array<{
    id: string;
    text: string;
    x: number;
    y: number;
    size: number;
    rotate: number;
    color: string;
    opacity: number;
    font: 'serif' | 'sans' | 'lanobe';
    isVertical: boolean;
  }>;
  bgTexture?: 'none' | 'parchment' | 'kraft' | 'washi' | 'watercolor' | 'canvas';
  bgTextureOpacity?: number;
  frameType?:
    | 'none'
    | 'classic-gold'
    | 'fancy-border'
    | 'japanese-border'
    | 'cyber-frame'
    | 'manuscript';
  frameColor?: string;
  frameOpacity?: number;
  activeEffects?: Array<{
    id: string;
    type: string;
    strength: number;
    color: string;
    scale?: number;
    rotate?: number;
  }>;
}

interface NovelCardProps {
  novel: Novel;
  index: number;
  novelHistory?: ReadingHistoryItem;
  onOpenDetail: (novel: Novel) => void;
  onDeleteNovel: (id: string, e: React.MouseEvent) => void;
  textMuted: string;
  isManualSortMode?: boolean;
  dragControls?: DragControls;
  coverConfig?: NovelCoverConfig;
  currentTheme?: NovelTheme;
}

export const NovelCard: React.FC<NovelCardProps> = ({
  novel,
  index,
  novelHistory,
  onOpenDetail,
  onDeleteNovel,
  textMuted,
  isManualSortMode = false,
  dragControls,
  coverConfig,
  currentTheme = 'paper',
}) => {
  const isCustom = novel.id.startsWith('custom-');
  const progress = novelHistory ? getDisplayProgressPercentage(novelHistory.progressPercentage) : 0;

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDeleteNovel(novel.id, event);
  };

  const isDark = isDarkTheme(currentTheme);

  // ベース背景色
  const cardContainerStyle: React.CSSProperties = {};
  if (coverConfig?.baseBgColor) {
    cardContainerStyle.backgroundColor = coverConfig.baseBgColor;
  }

  // ==========================================
  // 手動並び替えモード (リスト表示)
  // ==========================================
  if (isManualSortMode) {
    return (
      <div
        className={`relative flex items-center justify-between p-2.5 rounded-xl border ${
          isDark ? 'bg-[#111111] border-[#1F242E]' : 'bg-[#FFFFFF] border-[#EAEAEA]'
        } shadow-xs w-full select-none gap-3`}
      >
        {/* 小さなカバープレビュー */}
        <NovelCover novel={novel} config={coverConfig} isSmall className="w-10 shrink-0" />

        {/* 小説タイトルと著者 */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: Click handler on card body is standard here */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpenDetail(novel)}>
          <h4 className="font-serif text-xs font-bold truncate leading-snug">{novel.title}</h4>
          <p className={`text-[10px] ${textMuted} truncate mt-0.5`}>{novel.author}</p>
        </div>

        {/* 右側操作部（削除ボタン ＆ ドラッグハンドル） */}
        <div className="flex items-center space-x-1 shrink-0">
          {isCustom && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400/70 hover:text-red-400 active:scale-95 transition-colors"
              aria-label="削除"
            >
              <TrashIcon size={14} />
            </button>
          )}

          {/* ドラッグハンドル */}
          <div
            onPointerDown={(e) => dragControls?.start(e)}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-neutral-450 dark:text-neutral-550 active:text-amber-500 cursor-grab active:cursor-grabbing hover:bg-neutral-500/5 transition-colors"
            style={{ touchAction: 'none' }}
            aria-label="ドラッグして並び替え"
          >
            <GripVerticalIcon size={16} />
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // 通常モード (グリッド表示)
  // ==========================================
  return (
    <motion.div
      layout={!isManualSortMode ? 'position' : undefined}
      initial={{ opacity: 0, y: 12, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.95 }}
      transition={SPRING_BOUNCY}
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.93 }}
      onClick={() => onOpenDetail(novel)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenDetail(novel);
        }
      }}
      // biome-ignore lint/a11y/useSemanticElements: needed for interactive bookshelf grid card
      role="button"
      tabIndex={0}
      aria-label={`${novel.title}の詳細を開く`}
      className="group relative flex min-h-11 w-full min-w-[min(100%,9.5rem)] cursor-pointer flex-col items-center rounded-xl p-1 outline-none transition focus-visible:ring-2 focus-visible:ring-amber-300/80 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 gpu-accelerated"
    >
      {isCustom && (
        <button
          type="button"
          aria-label={`${novel.title}を削除`}
          onClick={handleDelete}
          className="absolute right-1 top-1 z-30 flex min-h-11 min-w-11 items-start justify-end rounded-xl p-1.5 text-stone-200/45 transition hover:text-red-300 focus-visible:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/70 active:scale-95"
        >
          <span className="rounded-full bg-black/15 p-1.5 opacity-70 backdrop-blur-xs transition group-hover:bg-black/35 group-hover:opacity-100">
            <TrashIcon size={11} strokeWidth={2} />
          </span>
        </button>
      )}

      <div
        style={cardContainerStyle}
        className={`relative aspect-[3/4.2] w-full overflow-hidden rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.18)] transition-all duration-300 flex flex-col justify-between ${
          coverConfig?.isPolaroid
            ? 'p-2.5 bg-white border border-stone-200 text-stone-950 shadow-sm'
            : 'p-3.5'
        } group-hover:-translate-y-1 group-hover:shadow-[0_8px_20px_rgba(0,0,0,0.25)] ${
          coverConfig?.hasBorder
            ? 'border-4 border-double border-white/20'
            : 'border border-white/5'
        }`}
      >
        <NovelCover novel={novel} config={coverConfig} />

        <div className="absolute top-3.5 left-4 z-10 flex origin-top-left scale-90 flex-col items-start space-y-1">
          {novel.scraped && (
            <div className="rounded-xs bg-amber-500/90 px-1.5 py-0.5 text-[8px] font-bold tracking-wider text-white shadow-xs backdrop-blur-xs">
              なろう
            </div>
          )}
          {novel.status && (
            <div
              className={`rounded-xs px-1.5 py-0.5 text-[8px] font-bold tracking-wider text-white shadow-xs backdrop-blur-xs ${novel.status === 'completed' ? 'bg-emerald-600/90' : novel.status === 'ongoing' ? 'bg-blue-600/90' : 'bg-stone-500/90'}`}
            >
              {novel.status === 'completed'
                ? '完結済'
                : novel.status === 'ongoing'
                  ? '連載中'
                  : '短編'}
            </div>
          )}
        </div>
        <div className="mt-auto w-full space-y-1.5 z-10">
          {novelHistory && (
            <div
              className="space-y-1"
              aria-label={`続きから読む、${novelHistory.sectionTitle || '本文'}の章内${progress}%位置`}
            >
              <div className="flex items-center justify-between rounded-full bg-black/35 px-2 py-1 text-[9px] font-bold tracking-tight text-stone-100 backdrop-blur-xs">
                <span>続きから読む</span>
                <span className="max-w-[56%] truncate text-right font-mono">
                  {novelHistory.sectionTitle ? `${novelHistory.sectionTitle} · ` : ''}章内{' '}
                  {progress}%
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-black/30">
                <div
                  className="h-full rounded-full bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.45)]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1">
            {!novelHistory && (
              <div className="rounded-full bg-black/15 px-2 py-0.5 font-serif text-[8px] text-stone-300/60">
                未読
              </div>
            )}
            {novel.episodesCount && (
              <div className="rounded-full bg-black/30 px-1.5 py-0.5 font-mono text-[8px] font-bold tracking-tight text-stone-200/90 backdrop-blur-xs">
                {novel.episodesCount}話
              </div>
            )}
            {novel.charCount && (
              <div className="rounded-full bg-black/35 px-1.5 py-0.5 font-mono text-[8px] font-bold tracking-tight text-stone-200/90 backdrop-blur-xs">
                {novel.charCount >= 10000
                  ? `${(novel.charCount / 10000).toFixed(1)}万字`
                  : `${novel.charCount}字`}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2.5 w-full px-1 text-center">
        <h2 className="truncate font-serif text-sm font-bold leading-tight tracking-wider">
          {novel.title}
        </h2>
        <p className={`mt-0.5 truncate text-xs ${textMuted}`}>{novel.author}</p>
      </div>
    </motion.div>
  );
};
